import type { WorkingPattern } from '@/types/timesheet';

export const ALL_DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
export const ALL_DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export type DayName = typeof ALL_DAYS[number];

export const COLUMN_WIDTHS_STORAGE_KEY = 'timesheetGrid.columnWidths';
export const FALLBACK_MIN_COLUMN_WIDTH = 80;
// Each column's floor is the non-shrinkable chrome of the control in its body cells
// (horizontal padding, borders, chevron/clear icons, favourite star) plus the cell
// padding, plus a small allowance so a truncated label is still readable. Dragging
// below this makes the control overflow into the neighbouring column.
export const MIN_COLUMN_WIDTHS: Record<string, number> = {
  project: 130,
  costCentre: 120,
  role: 100,
  workType: 110,
  description: 120,
};
// Relative weights used to share the space left by the fixed-width day/Total/Actions
// columns between the columns the user has not resized.
export const COLUMN_WIDTH_WEIGHTS: Record<string, number> = {
  project: 160,
  costCentre: 130,
  role: 110,
  workType: 120,
  description: 200,
};
export const RESIZABLE_COLUMN_KEYS = ['project', 'costCentre', 'role', 'workType', 'description'] as const;

export function getMinColumnWidth(columnKey: string): number {
  return MIN_COLUMN_WIDTHS[columnKey] ?? FALLBACK_MIN_COLUMN_WIDTH;
}

export function buildDaysFromStart(
  weekStartDay: 0 | 1 | 2 | 3 | 4 | 5 | 6,
  workingDays?: Record<string, { isWorkingDay: boolean }>
) {
  const days: DayName[] = [];
  const labels: string[] = [];
  
  // Build all 7 days starting from weekStartDay
  for (let i = 0; i < 7; i++) {
    const idx = (weekStartDay + i) % 7;
    const day = ALL_DAYS[idx];
    
    // If workingDays config exists, only include working days
    // Otherwise, include all weekdays (exclude weekends by default)
    if (workingDays) {
      const isWorking = workingDays[day]?.isWorkingDay;
      if (isWorking) {
        days.push(day);
        labels.push(ALL_DAY_LABELS[idx]);
      } else {
      }
    } else if (day !== 'saturday' && day !== 'sunday') {
      days.push(day);
      labels.push(ALL_DAY_LABELS[idx]);
    }
  }
  
  return { days, labels };
}

/**
 * Build the set of days that are in the company's working pattern but NOT
 * in the employee's working pattern. These days should still appear in the
 * timesheet grid (so the Non-Working Day Entry policy check can fire) but
 * be visually marked as non-working.
 */
export function getNonWorkingDaysForEmployee(
  companyWorkingDays: Record<string, { isWorkingDay: boolean }> | undefined,
  employeeWorkingPattern: WorkingPattern | undefined
): Set<DayName> {
  const nonWorking = new Set<DayName>();
  if (!companyWorkingDays || !employeeWorkingPattern?.workingDays) return nonWorking;

  for (const day of ALL_DAYS) {
    const companyWorking = companyWorkingDays[day]?.isWorkingDay ?? false;
    const employeeWorking = employeeWorkingPattern.workingDays[day] ?? false;
    if (companyWorking && !employeeWorking) {
      nonWorking.add(day);
    }
  }
  return nonWorking;
}

export function isWeekendDay(day: DayName): boolean {
  return day === 'saturday' || day === 'sunday';
}
